<script setup>
import { ref, computed, onMounted } from 'vue'
import Container from '@/mini/components/Container.vue'
import MessageBox from '@/mini/components/MessageBox.vue'
import { useMessage } from '@/mini/composables/useMessage'
import Boxes from '@/mini/components/Boxes.vue'
import Box from '@/mini/components/Box.vue'
import Button from '@/mini/components/Button.vue'
import Space from '@/mini/components/Space.vue'
import Sep from '@/mini/components/Sep.vue'
import CardCreateModal from '@/components/CardCreateModal.vue'
import CardEditModal from '@/components/CardEditModal.vue'
import CardView from '@/components/CardView.vue'
import { normalizeCard } from '@/api/cardApi'
import { useCardtrader } from '@/composables/useCardtrader'
import { useModalRouting } from '@/composables/useModalRouting'
import { useAuthStore } from '@/mini/stores/auth'
import { useRouter, useRoute } from 'vue-router'
import { BACKEND_ORIGIN } from '@/config/api'
import { normalizeSetType, getCardApiBaseBySetType, normalizeApiPrice, getCandidateImage, getFetchOptionLabel } from '@/utils/cardFormUtils'

const OPTCG_API_BASE_URL = import.meta.env.VITE_OPTCG_API_BASE_URL
const GAME_ID = 15

const { showDangerMessage, showSuccessMessage } = useMessage()
const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()

async function authenticatedApiFetch(path, options = {}) {
  try {
    const url = path.startsWith('http') ? path : `${BACKEND_ORIGIN}${path}`
    const response = await authStore.authFetch(url, options)
    if (response.status === 401) {
      authStore.logout()
      router.replace({ name: 'login' })
      throw new Error('Session expired')
    }
    return response
  } catch (error) {
    if (error instanceof Error && error.message === 'Not authenticated') {
      router.replace({ name: 'login' })
    }
    throw error
  }
}

const { saveCtPrice } = useCardtrader(authenticatedApiFetch)

const expansions = ref([])
const loading = ref(false)

async function fetchExpansions() {
  loading.value = true
  expansions.value = []

  try {
    const response = await authStore.authFetch(`${BACKEND_ORIGIN}/api/v1/ct/expansions/`)

    if (!response.ok) {
      throw new Error(`Request failed: ${response.status} ${response.statusText}`)
    }

    const data = await response.json()
    expansions.value = data.filter(exp => exp.game_id === GAME_ID)

    await saveExpansions()
  } catch (err) {
    showDangerMessage(err.message)
  } finally {
    loading.value = false
  }
}

async function saveExpansions() {
  const cardtrader_extensions = expansions.value.map(({ id, name, code }) => ({ id, name, code: code?.replace(/-/g, '') }))
  try {
    window.localStorage.setItem('optcg.cardtrader_extensions', JSON.stringify(cardtrader_extensions))
    showSuccessMessage(`${cardtrader_extensions.length} expansions saved.`)
  } catch (err) {
    throw new Error(`Failed to save: ${err.message}`)
  }
}

async function loadExpansions() {
  loading.value = true
  try {
    const stored = window.localStorage.getItem('optcg.cardtrader_extensions')
    expansions.value = stored ? JSON.parse(stored) : []
  } catch (err) {
    showDangerMessage(err.message)
  } finally {
    loading.value = false
  }
}

const ownedCards = ref([])
const ownedCardsLoading = ref(false)

async function loadOwnedCards() {
  ownedCardsLoading.value = true
  try {
    const response = await authenticatedApiFetch('/api/v1/cards/owned-index/')
    if (!response.ok) throw new Error(`Load failed: ${response.status} ${response.statusText}`)
    ownedCards.value = await response.json()
  } catch (err) {
    showDangerMessage(err.message)
  } finally {
    ownedCardsLoading.value = false
  }
}

const createModalPrefill = ref(null)
const { activeModal, modalCard, openModal, closeModal } = useModalRouting()

async function openCardView(ctCard) {
  if (!getMatchingCards(ctCard).length) return
  const param = ctCard.blueprint_id ?? encodeURIComponent(ctCard.collector_number)
  try {
    const res = await authenticatedApiFetch(`/api/v1/cards/?search=${param}&page_size=1`)
    if (!res.ok) return
    const data = await res.json()
    const card = (data.results ?? data)[0]
    if (card) { openModal('view', normalizeCard(card)) }
  } catch {}
}

function parsePrefill(card) {
  const cn = card.collector_number ?? ''
  const dashIdx = cn.lastIndexOf('-')
  const setId = dashIdx > 0 ? cn.slice(0, dashIdx) : cn
  const cardNumber = (dashIdx > 0 ? cn.slice(dashIdx + 1) : '').replace(/[a-z]+$/i, '')
  const setType = setId.replace(/\d+$/, '') || 'OP'
  return { set_type: setType, set_id: setId, card_number: cardNumber, to_buy: true, group_name: setId, cardtrader_blueprint: card.blueprint_id ?? null, cardtrader_expansion: card.expansion_id ?? null }
}

function openWishlist(card) {
  createModalPrefill.value = parsePrefill(card)
  openModal('create')
}

const selectedExpansion = ref(null)
const expansionProducts = ref([])
const expansionProductsLoading = ref(false)
const filterLessThan50 = ref(false)
const filterLessThan50Qty = ref(30)
const filterLessThan25 = ref(false)
const filterLessThan25Price = ref(15)
const filterNotOwned = ref(false)
const filterWishlist = ref(true)
const expansionsCollapsed = ref(false)

function getMatchingCards(card, wishlistOnly = null) {
  const letterSuffixMatch = card.collector_number?.match(/^(.+)-(\d+)([a-z]+)$/i)
  return ownedCards.value.filter(p => {
    if (wishlistOnly !== null && p.wishlist !== wishlistOnly) return false
    // If our card has a blueprint, only match by exact blueprint — card_id alone is not enough
    // since the same number can have multiple CT versions with different blueprints
    if (p.blueprint) return p.blueprint === card.blueprint_id
    // No blueprint stored — fall back to card_id matching
    if (p.card_id === card.collector_number) return true
    // CT has suffix (e.g. OP06-001a) — only match base if DB card name signals alternate/parallel
    if (letterSuffixMatch) {
      const baseCode = `${letterSuffixMatch[1]}-${letterSuffixMatch[2]}`
      if (p.card_id === baseCode && /alternate|parallel/i.test(p.card_name ?? '')) return true
    }
    return false
  })
}

function getMatchingOwned(card) { return getMatchingCards(card, false) }
function getMatchingWishlist(card) { return getMatchingCards(card, true) }

function getOwnedQuantity(card) {
  return getMatchingOwned(card).length
}

function getWishlistQuantity(card) {
  return getMatchingWishlist(card).length
}

function getDbLowestPrice(card) {
  const prices = getMatchingOwned(card)
    .map(p => Number(p.lowest_ct_price))
    .filter(p => Number.isFinite(p) && p > 0)
  return prices.length ? Math.min(...prices) : null
}

const filteredExpansionProducts = computed(() => {
  return expansionProducts.value
    .map(card => ({ ...card, ownedQuantity: getOwnedQuantity(card), wishlistQuantity: getWishlistQuantity(card), dbLowestPrice: getDbLowestPrice(card) }))
    .filter(card => {
      if (filterLessThan50.value && card.totalQuantity >= filterLessThan50Qty.value) return false
      if (filterLessThan25.value && card.lowestPrice >= filterLessThan25Price.value * 100) return false
      if (filterNotOwned.value && card.ownedQuantity > 0) return false
      if (!filterWishlist.value && card.wishlistQuantity > 0) return false
      return true
    })
})

const marketPrices = ref({}) // keyed by collector_number → { price, fromBase }
const marketPricesLoading = ref(false)
const marketPricesProgress = ref({ done: 0, total: 0, errors: 0 })

async function fetchCardPrice(collectorNumber, cardName) {
  const setType = normalizeSetType('', collectorNumber)
  const base = getCardApiBaseBySetType(setType)
  if (!base) return { price: null, candidates: [] }

  const response = await fetch(`${OPTCG_API_BASE_URL}/${base}/card/${encodeURIComponent(collectorNumber)}/`)
  if (!response.ok) throw new Error('HTTP ' + response.status)
  const data = await response.json()
  const candidates = (Array.isArray(data) ? data : [data]).filter(item => item && typeof item === 'object')
  if (!candidates.length) return { price: null, candidates: [] }
  if (candidates.length > 1) {
    // Return all candidates so the user can pick
    const price = normalizeApiPrice(candidates[0].market_price ?? candidates[0].marketPrice ?? candidates[0].price)
    return { price: price !== '' ? price : null, candidates }
  }
  const price = normalizeApiPrice(candidates[0].market_price ?? candidates[0].marketPrice ?? candidates[0].price)
  return { price: price !== '' ? price : null, candidates }
}

async function fetchAllMarketPrices() {
  if (marketPricesLoading.value) return
  const cards = filteredExpansionProducts.value.filter(c => c.collector_number)
  if (!cards.length) return

  marketPricesLoading.value = true
  marketPricesProgress.value = { done: 0, total: cards.length, errors: 0 }
  marketPrices.value = {}

  for (const card of cards) {
    try {
      let result = { price: null, candidates: [] }
      let fromBase = false

      try { result = await fetchCardPrice(card.collector_number, card.name) } catch { /* fall through to base code */ }

      // If no candidates, try stripping trailing letter suffix (e.g. "OP15-001a" → "OP15-001")
      const letterSuffixMatch = card.collector_number.match(/-(\d+)([a-z]+)$/i)
      if (!result.candidates.length && letterSuffixMatch) {
        const baseCode = card.collector_number.replace(/-(\d+)[a-z]+$/i, '-$1')
        try {
          result = await fetchCardPrice(baseCode, card.name)
          if (result.candidates.length) fromBase = true
        } catch { /* leave as null */ }
      }

      // Auto-select the "Alternate" candidate when the collector number has a letter suffix
      let selectedIndex = 0
      let fallbackNote = fromBase ? 'base card' : null
      if (fromBase && letterSuffixMatch && result.candidates.length > 1) {
        const altIndex = result.candidates.findIndex(c =>
          /alternate|parallel/i.test(c.name ?? c.name_en ?? c.card_name ?? '')
        )
        if (altIndex !== -1) {
          selectedIndex = altIndex
          const candidate = result.candidates[altIndex]
          const price = normalizeApiPrice(candidate.market_price ?? candidate.marketPrice ?? candidate.price)
          result.price = price !== '' ? price : null
          const matchedName = candidate.name ?? candidate.name_en ?? candidate.card_name ?? ''
          fallbackNote = /parallel/i.test(matchedName) ? 'parallel' : 'alternate art'
        }
      }

      marketPrices.value[card.blueprint_id] = {
        price: result.price,
        fromBase,
        fallbackNote,
        candidates: result.candidates,
        selectedIndex,
        showPicker: false,
      }
    } catch {
      marketPricesProgress.value.errors++
      marketPrices.value[card.blueprint_id] = { price: null, fromBase: false, fallbackNote: null, candidates: [], selectedIndex: 0, showPicker: false }
    }
    marketPricesProgress.value.done++
  }

  marketPricesLoading.value = false
  if (marketPricesProgress.value.errors > 0) {
    showDangerMessage(`Done with ${marketPricesProgress.value.errors} error(s).`)
  } else {
    showSuccessMessage('Market prices fetched.')
  }
}

function togglePicker(collectorNumber) {
  const entry = marketPrices.value[collectorNumber]
  if (!entry) return
  marketPrices.value[collectorNumber] = { ...entry, showPicker: !entry.showPicker }
}

function selectCandidate(collectorNumber, index) {
  const entry = marketPrices.value[collectorNumber]
  if (!entry) return
  const candidate = entry.candidates[index]
  const price = normalizeApiPrice(candidate.market_price ?? candidate.marketPrice ?? candidate.price)
  entry.selectedIndex = index
  entry.price = price !== '' ? price : null
  entry.showPicker = false
}

async function loadExpansionProducts(expansion) {
  selectedExpansion.value = expansion
  expansionsCollapsed.value = true
  router.replace({ name: 'buy', params: { expansionCode: expansion.code?.replace(/-/g, '') ?? expansion.id } })
  if (!ownedCards.value.length) loadOwnedCards()
  expansionProductsLoading.value = true
  expansionProducts.value = []

  try {
    const response = await authStore.authFetch(`${BACKEND_ORIGIN}/api/v1/ct/marketplace/?expansion_id=${expansion.id}`)
    if (!response.ok) throw new Error(`Request failed: ${response.status} ${response.statusText}`)
    const data = await response.json()
    
    // API returns an object where keys are blueprint_ids and values are arrays of products
    // Flatten all products into a single array
    const allProducts = Object.values(data).flat()
    
    // Filter for Near Mint condition, Japanese language, and can_sell_sealed_with_ct_zero only
    const filteredProducts = allProducts.filter(product => 
      product.properties_hash?.condition === 'Near Mint' &&
      product.properties_hash?.onepiece_language === 'jp' &&
      product.user.can_sell_sealed_with_ct_zero === true
    )

    // Group by blueprint_id and aggregate quantities
    const groupedMap = {}
    filteredProducts.forEach(product => {
      const key = product.blueprint_id
      if (!groupedMap[key]) {
        groupedMap[key] = {
          blueprint_id: product.blueprint_id,
          name: product.name_en ?? product.name,
          collector_number: product.properties_hash?.collector_number,
          expansion_id: product.expansion?.id,
          slug: product.slug ?? null,
          totalQuantity: 0,
          lowestPrice: product.price_cents,
          products: [],
          ownedQuantity: 0
        }
      }
      groupedMap[key].totalQuantity += product.quantity
      groupedMap[key].lowestPrice = Math.min(groupedMap[key].lowestPrice, product.price_cents)
      groupedMap[key].products.push(product)
    })

    expansionProducts.value = Object.values(groupedMap)
      .sort((a, b) => (a.collector_number ?? '').localeCompare(b.collector_number ?? '', undefined, { numeric: true }))
  } catch (err) {
    showDangerMessage(err.message)
  } finally {
    expansionProductsLoading.value = false
  }
}

const availableGroups = ref([])

onMounted(async () => {
  await loadExpansions()
  if (route.params.expansionCode) {
    const code = String(route.params.expansionCode).toUpperCase()
    const match = expansions.value.find(e => e.code?.replace(/-/g, '').toUpperCase() === code)
    if (match) loadExpansionProducts(match)
  }
  const groupsRes = await authenticatedApiFetch('/api/v1/groups/?page_size=500')
  if (groupsRes.ok) {
    const data = await groupsRes.json()
    availableGroups.value = data.results ?? data
  }
})
</script>

<template>
  <MessageBox />
  <Container>
    <Boxes spaceTopBot fh class="align-content-start">

        <Box size="100">
          <Space height="3"/>
          <h1 class="light m-0">Cards to buy</h1>
        </Box>

        <Box size="100" class="">
            <h3 class="m-0 light pointer" :class="[expansionsCollapsed ? 'underline' : '']" style="user-select:none;" @click="expansionsCollapsed = !expansionsCollapsed">
                Expansions list
            </h3>
            <p v-if="expansions.length" class="m-0"><b>{{ expansions.length }}</b> expansion{{ expansions.length !== 1 ? 's' : '' }} found for <b>One Piece The Card Game</b> (game_id {{ GAME_ID }})</p>
            <Sep class="my-2 light-grey-border"/>

            <template v-if="expansionsCollapsed && selectedExpansion">
                  <div class="boxes align-items-start">
                    <div class="box-100 pointer b-rad-5 color-bg">
                        <p class="lh-15 m-0 white-text">
                            <b>{{ selectedExpansion.code?.toUpperCase() }}</b>&nbsp;&nbsp;<span class="light-grey-text">(id: {{ selectedExpansion.id }})</span><br/>
                            <b>{{ selectedExpansion.name }}</b>
                        </p>
                    </div>
                  </div>
            </template>

            <template v-if="!expansionsCollapsed">
                <template v-if="expansions.length">
                  <div class="boxes align-items-start">
                    <div class="box-25"
                      v-for="exp in expansions"
                      :key="exp.id"
                      :class="['pointer b-rad-5', selectedExpansion?.id === exp.id ? 'color-bg' : '']"
                      @click="loadExpansionProducts(exp)"
                      >
                      <p
                          :class="['lh-15 m-0', selectedExpansion?.id === exp.id ? 'white-text' : '']"
                          >
                          <b>{{ exp.code?.toUpperCase() }}</b>&nbsp;&nbsp;<span class="light-grey-text">(id: {{ exp.id }})</span><br/>
                          <b>{{ exp.name }}</b>
                      </p>
                    </div>
                  </div>
                </template>
                <Sep class="my-2 light-grey-border"/>
                <p class="">
                    <Button @click="fetchExpansions" :disabled="loading" color="blue" class="round px-15" invert>
                        {{ loading ? 'Loading...' : 'Fetch and save expansions' }}
                    </Button>
                </p>
            </template>
        </Box>

        <Box v-if="selectedExpansion" size="100" class="">
            <h3 class="mb-0">{{ selectedExpansion.name }}</h3>
            <p v-if="expansionProductsLoading" class="mt-0">Loading...</p>
            <p v-if="!expansionProductsLoading && expansionProducts.length" class="mt-0">
                <b>{{ filteredExpansionProducts.length }}</b> unique card{{ filteredExpansionProducts.length !== 1 ? 's' : '' }},
                <b>{{ filteredExpansionProducts.reduce((sum, card) => sum + card.totalQuantity, 0) }}</b> total on sale
            </p>
            <p v-if="!expansionProductsLoading && !expansionProducts.length" class="mt-0">No products found for this expansion.</p>
            <div class="boxes fw-bg b-rad-10 p-15 pb-1 my-2" v-if="selectedExpansion">
              <Box size="25">
                <label class="flex align-items-center">
                    <input type="checkbox" class="ms-1" v-model="filterWishlist" />&nbsp;Wishlisted cards
                </label>
              </Box>
              <Box size="25">
                <label class="flex align-items-center">
                    <input type="checkbox" class="ms-1" v-model="filterNotOwned" />&nbsp;Not owned only
                </label>
              </Box>
              <Box size="25">
                <label class="flex align-items-center">
                    <input type="checkbox" class="ms-1" v-model="filterLessThan50" />&nbsp;Less than&nbsp;&nbsp;
                    <input type="number" v-model.number="filterLessThan50Qty" min="1" style="width:60px;" />&nbsp;
                    sellers
                </label>
              </Box>
              <Box size="25">
                <label class="flex align-items-center">
                    <input type="checkbox" class="ms-1" v-model="filterLessThan25" />&nbsp;Less than&nbsp; &nbsp;
                    <input type="number" v-model.number="filterLessThan25Price" min="0" step="0.01" style="width:70px;" />&nbsp;
                    €
                </label>
              </Box>
            </div>
            <Boxes>
                <Box size="33"
                  v-for="card in filteredExpansionProducts"
                  :key="card.blueprint_id"
                >
                    <p class="m-0">
                        <span class=""><span class="bad-text">{{ card.totalQuantity }}</span>x</span>&nbsp;&nbsp;
                        <span v-if="card.ownedQuantity > 0 || card.wishlistQuantity > 0" class="underline bk-text cursor-pointer" @click="openCardView(card)"><b>{{ card.name }}</b></span>
                        <span v-else><b>{{ card.name }}</b></span>
                        <span v-if="card.ownedQuantity > 0" class="white-box success-border b-rad-3 flag ms-05">&nbsp;<span class="success-text">Owned</span>&nbsp;&nbsp;({{ card.ownedQuantity }})&nbsp;</span>
                        <template v-else-if="card.wishlistQuantity > 0">
                          <span class="warning-box-invert b-rad-3 flag ms-05">&nbsp;In wishlist&nbsp;</span>
                        </template>
                        <template v-else>
                          <span class="bad-box-invert b-rad-3 flag ms-05">&nbsp;Not owned&nbsp;</span>
                        </template>
                        <span v-if="card.blueprint_id in marketPrices && marketPrices[card.blueprint_id].price !== null && card.lowestPrice / 100 < marketPrices[card.blueprint_id].price / 3"
                            class="flag second-color-box b-rad-3 bold"
                            >&nbsp;🔥&nbsp;Must have&nbsp;</span>
                    </p>
                    <p class="m-0 S">
                        <span class="S">{{ card.collector_number }}</span><span v-if="card.blueprint_id" class="XS ms-05">(<a :href="`https://www.cardtrader.com/en/cards/${card.blueprint_id}`" target="_blank" class="blue-text">{{ card.blueprint_id }}</a>)
                        </span>&nbsp;&nbsp;<span class="light-grey-text">|</span>&nbsp;&nbsp;
                        Price: <b>{{ (card.lowestPrice / 100).toFixed(2) }}€</b>
                        <span v-if="Number(card.dbLowestPrice)>=Number(card.lowestPrice)/100" class="flag white-bg blue-text border blue-border b-rad-3 ms-05" style="font-size: calc(var(--p) * 0.6)!important;"><a :href="`https://www.cardtrader.com/en/cards/${card.blueprint_id}`" target="_blank" class="blue-text">lowest</a></span>
                        <template v-if="card.blueprint_id in marketPrices">
                            &nbsp;&nbsp;<span class="light-grey-text">|</span>&nbsp;&nbsp;max value:
                            <b v-if="marketPrices[card.blueprint_id].price !== null">{{ marketPrices[card.blueprint_id].price }}€</b>
                            <span v-else class="warning-text">N/A</span>
                            <span v-if="marketPrices[card.blueprint_id].fallbackNote" class="S" style="opacity:0.6">&nbsp;({{ marketPrices[card.blueprint_id].fallbackNote }})</span>
                            <span
                                v-if="marketPrices[card.blueprint_id].candidates.length > 1"
                                class="underline XS ms-1 warning-text pointer"
                                @click="togglePicker(card.blueprint_id)"
                            ><br/>
                            {{ marketPrices[card.blueprint_id].showPicker ? 'Hide' : 'Choose version' }}</span>
                        </template>
                    </p>
                    <p v-if="card.dbLowestPrice" class="m-0 XS">
                      <span v-if="card.lowestPrice"><span class="blue-text">CT lowest price</span>: <b>{{Number(card.dbLowestPrice).toFixed(2)}}€</b></span>
                    </p>
                    <template v-if="marketPrices[card.blueprint_id]?.showPicker">
                        <Boxes class="pt-05 pb-1">
                            <Box
                                v-for="(candidate, index) in marketPrices[card.blueprint_id].candidates"
                                :key="'cand-' + card.collector_number + '-' + index"
                                padding="05"
                                class="fw-bg b-rad-10"
                                style="cursor: pointer; width: 130px; text-align: center;"
                                :class="marketPrices[card.blueprint_id].selectedIndex === index ? 'bk-bg' : ''"
                                @click="selectCandidate(card.blueprint_id, index)"
                            >
                                <img v-if="getCandidateImage(candidate)" :src="getCandidateImage(candidate)" style="width:100%; border-radius:6px;" class="mb-05" />
                                <p class="S m-0" :class="marketPrices[card.blueprint_id].selectedIndex === index ? 'wh-text' : ''">{{ getFetchOptionLabel(candidate, index) }}</p>
                            </Box>
                        </Boxes>
                    </template>
                    <p v-if="!card.wishlistQuantity && !card.ownedQuantity" class="my-05">
                      <Button size="XS" color="warning" class="round" @click="openWishlist(card)">+ Wishlist</Button>
                    </p>
                </Box>
            </Boxes>
            <p class="">
                <Button color="bad" class="round px-15" :disabled="marketPricesLoading" @click="fetchAllMarketPrices">
                    {{ marketPricesLoading ? `Fetching… ${marketPricesProgress.done}/${marketPricesProgress.total}` : 'Fetch market prices' }}
                </Button>
            </p>
        </Box>

    </Boxes>
  </Container>
  <CardCreateModal :visible="activeModal === 'create'" :prefill="createModalPrefill" :availableGroups="availableGroups" @close="closeModal()" @saved="loadOwnedCards()" />
  <CardView
    :visible="activeModal === 'view'"
    :card="modalCard"
    @close="closeModal()"
    @edit="card => openModal('edit', card)"
    @save-ct-price="saveCtPrice"
  />
  <CardEditModal
    :visible="activeModal === 'edit'"
    :card="modalCard"
    :availableGroups="availableGroups"
    @close="closeModal()"
    @saved="closeModal()"
    @deleted="closeModal()"
  />
</template>

<style scoped>
</style>
